/**
 * 3D Visualizer — Render Settings
 *
 * Everything about how the 3D Designer's view is LIT and FINISHED, as one flat
 * object of plain values: lights, shadows, ambient occlusion, materials, lens
 * effects, tone and quality. The render pipeline (renderPipeline.ts) turns
 * these into three.js state; the designer's render windows edit them live.
 *
 * Like framingSettings, this lives outside React: a tiny store with
 * subscribers, so moving a slider re-renders the 3D view without rebuilding the
 * scene. Values are kept in localStorage, so a look survives a reload — and can
 * be copied out as JSON to be made the default here.
 *
 * Units, so a slider means the same thing in any room:
 *   - light intensities are "how bright a white board gets" — 1.0 is full white
 *     where the light hits it square on (the pipeline converts to three's
 *     physical units, and scales the room light to the room's size);
 *   - warmth is colour temperature in kelvin (6500 = neutral white);
 *   - distances are feet, angles degrees, positions fractions of the room.
 *
 * No three.js in here: it is plain data, so jest can cover it.
 */

import defaultLook from './looks/default.json';

export type ToneMappingId = 'accurate' | 'linear' | 'neutral' | 'reinhard' | 'cineon' | 'aces' | 'agx';
export type ShadowTypeId = 'basic' | 'pcf' | 'pcfsoft' | 'vsm';
export type EnvironmentId = 'studio' | 'garage1' | 'garage2' | 'garage3' | 'none';
export type KeyLightMode = 'camera' | 'fixed';
export type LogoVersion = 'icon' | 'light' | 'dark';
/** How a room's lamps are laid out: four on the inner points of a 3×3 grid, a row down the room, or an array filling it (lampLayout.ts). */
export type LampLayout = 'grid' | 'row' | 'array';
/** What each lamp looks like: a dome light, a recessed pot light, or a shop-light bar. */
export type FixtureStyle = 'dome' | 'pot' | 'bar';
export type LogoPosition = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
/** What a wall or ceiling shows where no product is on it (room3d/bareFill.ts). */
export type BareFillId = 'studs' | 'drywall' | 'grid';

export interface RenderSettings {
  /** The Lighting window: the simple panel (a few choices that lay the room's lighting out) or every setting. */
  lightingSimple: boolean;

  // ── Room light (the lamps inside the room) ──
  interiorLayout: LampLayout;
  interiorLamps: number;
  /** Array layout: about one lamp every so many feet, each way. */
  interiorSpacing: number;
  interiorFixtures: boolean;
  interiorFixtureStyle: FixtureStyle;
  /** Floor plans: the other rooms' lamps cast shadows too (at a lower resolution). */
  interiorOtherShadows: boolean;
  interiorIntensity: number;
  interiorWarmth: number;
  interiorHeight: number;
  interiorX: number;
  interiorZ: number;
  interiorFalloff: number;
  interiorReach: number;

  // ── Ambient (light from everywhere) ──
  ambientIntensity: number;
  ambientSky: string;
  ambientGround: string;

  // ── Key light and its fill ──
  keyIntensity: number;
  keyMode: KeyLightMode;
  keyAzimuth: number;
  keyElevation: number;
  keyWarmth: number;
  fillIntensity: number;

  // ── Environment (what the boards reflect) ──
  environment: EnvironmentId;
  envIntensity: number;
  probeIntensity: number;

  // ── Shadows ──
  shadows: boolean;
  shadowType: ShadowTypeId;
  shadowSoftness: number;
  shadowResolution: number;
  shadowBias: number;
  shadowNormalBias: number;

  // ── Ambient occlusion ──
  ao: boolean;
  aoIntensity: number;
  aoRadius: number;
  aoFalloff: number;
  aoThickness: number;
  aoSamples: number;
  aoHalfRes: boolean;

  groundShadow: number;

  // ── Blueprint ground (the Floor Planner's sheet, under the rooms) ──
  blueprint: boolean;
  blueprintColor: string;
  blueprintOpacity: number;
  blueprintLineColor: string;
  blueprintLineOpacity: number;
  blueprintLineWidth: number;
  blueprintMajorEvery: number;
  blueprintMajorWeight: number;
  blueprintReach: number;

  // ── Materials ──
  /** Bare areas (no product): the studs with a sheet behind them, unpainted drywall, or a grid. */
  bareWallFill: BareFillId;
  bareCeilingFill: BareFillId;
  boardRoughness: number;
  boardMetalness: number;
  boardGlow: number;
  boardBump: number;
  seamStrength: number;
  seamWidth: number;
  seamColor: string;
  // ── Glass (windows, and the glazed parts of doors) ──
  glassTint: string;
  glassOpacity: number;
  glassReflection: number;
  glassGlare: number;
  glassGlareSize: number;

  floorRoughness: number;
  floorReflection: number;
  floorBrightness: number;
  /** A built-in floor (FLOOR_TEXTURES) or the id of an uploaded picture. */
  floorTexture: string;
  floorTextureSize: number;
  floorTextureRotate: boolean;

  // ── Framing lumber ──
  lumberColor: string;
  lumberGrainLines: number;
  lumberGrainContrast: number;
  lumberLineWidth: number;
  lumberWaviness: number;
  lumberFibre: number;
  lumberKnots: number;
  lumberKnotDarkness: number;
  lumberRelief: number;
  /** '' = the simulated grain; otherwise the id of an uploaded picture. */
  lumberTexture: string;
  lumberTextureSize: number;
  lumberTextureRotate: boolean;
  lumberTextureTint: number;

  // ── Wood doors ──
  doorWoodColor: string;
  doorGrainLines: number;
  doorGrainContrast: number;
  doorLineWidth: number;
  doorWaviness: number;
  doorFibre: number;
  doorRelief: number;
  doorTexture: string;
  doorTextureSize: number;
  doorTextureRotate: boolean;
  doorTextureTint: number;

  // ── Effects ──
  brightness: number;
  contrast: number;
  saturation: number;
  temperature: number;
  bloom: boolean;
  bloomStrength: number;
  bloomRadius: number;
  bloomThreshold: number;
  bloomKnee: number;
  vignette: number;
  vignetteSoftness: number;
  grain: number;
  sharpen: number;
  /** A lens's softness: the picture blurred a touch (0 none, 1 a few pixels), before any sharpening. */
  softness: number;
  /** The exposure metered from the picture (exposureMeter.ts), `exposure` an offset from it — as the render does. */
  autoExposure: boolean;
  backdropTop: string;
  backdropBottom: string;

  // ── Output ──
  toneMapping: ToneMappingId;
  exposure: number;
  highlightRolloff: number;
  renderScale: number;
  antialias: number;

  // ── Logo (drawn over the view, not in it) ──
  logo: boolean;
  logoVersion: LogoVersion;
  logoPosition: LogoPosition;
  logoScale: number;
  logoOpacity: number;
  logoMargin: number;
}

export type RenderSettingKey = keyof RenderSettings;

/**
 * The factory look — the studio look as coded, tuned so white reads white and
 * seams stay just visible. What looks/default.json and every exported recipe
 * are changes FROM. (Not what the designer opens with: see DEFAULT_RENDER_SETTINGS.)
 */
export const FACTORY_RENDER_SETTINGS: RenderSettings = {
  lightingSimple: true,
  interiorLayout: 'array',
  interiorLamps: 2,
  interiorSpacing: 11,
  interiorFixtures: true,
  interiorFixtureStyle: 'dome',
  interiorOtherShadows: true,
  interiorIntensity: 0.3,
  interiorWarmth: 6500,
  interiorHeight: 0.8,
  interiorX: 0.5,
  interiorZ: 0.5,
  interiorFalloff: 0.6,
  interiorReach: 0,

  ambientIntensity: 0.5,
  ambientSky: '#ffffff',
  ambientGround: '#f2f2f2',

  keyIntensity: 0.14,
  keyMode: 'camera',
  keyAzimuth: 35,
  keyElevation: 30,
  keyWarmth: 6500,
  fillIntensity: 0.05,

  environment: 'studio',
  envIntensity: 0.25,
  probeIntensity: 0,

  shadows: true,
  shadowType: 'vsm',
  shadowSoftness: 3,
  shadowResolution: 1024,
  shadowBias: -0.0027,
  shadowNormalBias: 0.085,

  ao: true,
  aoIntensity: 1,
  aoRadius: 1.5,
  aoFalloff: 1,
  aoThickness: 1,
  aoSamples: 16,
  aoHalfRes: true,

  groundShadow: 0.35,

  blueprint: true,
  blueprintColor: '#153e6e',
  blueprintOpacity: 0.9,
  blueprintLineColor: '#eaf2ff',
  blueprintLineOpacity: 0.35,
  blueprintLineWidth: 1,
  blueprintMajorEvery: 5,
  blueprintMajorWeight: 1.8,
  blueprintReach: 30,

  boardRoughness: 0.45,
  boardMetalness: 0,
  boardGlow: 0.06,
  boardBump: 0.5,
  seamStrength: 0.3,
  seamWidth: 0.125,
  seamColor: '#20242a',
  bareWallFill: 'studs',
  bareCeilingFill: 'studs',
  glassTint: '#d3e6f0',
  glassOpacity: 0.16,
  glassReflection: 3,
  glassGlare: 1.5,
  glassGlareSize: 0.06,

  floorRoughness: 0.9,
  floorReflection: 0.3,
  floorBrightness: 1,
  floorTexture: 'concrete-dirty',
  floorTextureSize: 7,
  floorTextureRotate: false,

  lumberColor: '#dcc096',
  lumberGrainLines: 8,
  lumberGrainContrast: 0.13,
  lumberLineWidth: 0.45,
  lumberWaviness: 0.16,
  lumberFibre: 0.05,
  lumberKnots: 4,
  lumberKnotDarkness: 0.22,
  lumberRelief: 0.3,
  lumberTexture: '',
  lumberTextureSize: 1,
  lumberTextureRotate: false,
  lumberTextureTint: 0,

  doorWoodColor: '#7a5540',
  doorGrainLines: 12,
  doorGrainContrast: 0.16,
  doorLineWidth: 0.45,
  doorWaviness: 0.22,
  doorFibre: 0.09,
  doorRelief: 0.5,
  doorTexture: '',
  doorTextureSize: 2,
  doorTextureRotate: false,
  doorTextureTint: 0,

  brightness: 0,
  contrast: 1,
  saturation: 1,
  temperature: 0,
  bloom: false,
  bloomStrength: 0.4,
  bloomRadius: 0.6,
  bloomThreshold: 0.8,
  bloomKnee: 1,
  vignette: 0.06,
  vignetteSoftness: 0.5,
  grain: 0.012,
  sharpen: 0.15,
  softness: 0,
  autoExposure: true,
  backdropTop: '#f9fafb',
  backdropBottom: '#e9ecf0',

  toneMapping: 'accurate',
  exposure: 1,
  highlightRolloff: 0.08,
  renderScale: 0,
  antialias: 4,

  logo: true,
  logoVersion: 'icon',
  logoPosition: 'bottom-left',
  logoScale: 1,
  logoOpacity: 0.9,
  logoMargin: 18,
};

/**
 * A recipe file's changes over a base look: every known key whose value is of
 * the right kind. (No clamping here — the sanitiser needs the controls, which
 * are defined below; the test suite checks the shipped default sanitises to itself.)
 */
function applyLookFile(base: RenderSettings, changes: Record<string, unknown>): RenderSettings {
  const out = { ...base } as unknown as Record<string, unknown>;
  for (const key of Object.keys(base)) {
    if (key in changes && typeof changes[key] === typeof out[key]) out[key] = changes[key];
  }
  return out as unknown as RenderSettings;
}

/**
 * The look the designer opens with: the factory look with the shipped recipe,
 * looks/default.json, applied. To change it: tune the look in the designer,
 * Export from the Looks panel, paste the whole file over looks/default.json.
 */
export const DEFAULT_RENDER_SETTINGS: RenderSettings = applyLookFile(FACTORY_RENDER_SETTINGS, defaultLook.changes);

/**
 * Settings that belong to the screen and the machine rather than to a look: the
 * logo, and how hard the GPU is worked. Choosing a look leaves them as they are,
 * and they don't count when deciding which look is in use.
 */
export const LOOK_INDEPENDENT_KEYS: RenderSettingKey[] = [
  'logo', 'logoVersion', 'logoPosition', 'logoScale', 'logoOpacity', 'logoMargin',
  'renderScale', 'antialias',
  // What things are made of is not lighting: a look leaves it alone too.
  'bareWallFill', 'bareCeilingFill',
  'glassTint', 'glassOpacity', 'glassReflection', 'glassGlare', 'glassGlareSize',
  'floorTexture', 'floorTextureSize', 'floorTextureRotate',
  'lumberColor', 'lumberGrainLines', 'lumberGrainContrast', 'lumberLineWidth', 'lumberWaviness', 'lumberFibre',
  'lumberKnots', 'lumberKnotDarkness', 'lumberRelief', 'lumberTexture', 'lumberTextureSize', 'lumberTextureRotate', 'lumberTextureTint',
  'doorWoodColor', 'doorGrainLines', 'doorGrainContrast', 'doorLineWidth', 'doorWaviness', 'doorFibre', 'doorRelief',
  'doorTexture', 'doorTextureSize', 'doorTextureRotate', 'doorTextureTint',
  // How a room is lit with lamps is part of the room, not of a look — and
  // which lighting panel is up is a preference.
  'lightingSimple', 'interiorLayout', 'interiorFixtureStyle', 'interiorSpacing', 'interiorOtherShadows',
  // The ground the rooms stand on is a choice of its own, like the logo.
  'blueprint', 'blueprintColor', 'blueprintOpacity', 'blueprintLineColor', 'blueprintLineOpacity',
  'blueprintLineWidth', 'blueprintMajorEvery', 'blueprintMajorWeight', 'blueprintReach',
];

/** The floors that ship with the app (public/textures). Anything else in `floorTexture` is an upload's id. */
export const FLOOR_TEXTURES: { value: string; label: string; file: string }[] = [
  { value: 'concrete-dirty', label: 'Concrete — worn', file: 'floor-concrete-dirty.jpg' },
  { value: 'concrete-bare', label: 'Concrete — bare', file: 'floor-concrete-bare.jpg' },
  { value: 'wood-light', label: 'Wood — light', file: 'floor-wood-light.jpg' },
  { value: 'wood-medium', label: 'Wood — medium', file: 'floor-wood-medium.jpg' },
  { value: 'wood-dark', label: 'Wood — dark', file: 'floor-wood-dark.jpg' },
];

// ============================================================================
// CONTROLS — what the render windows show, and the limits every value is held to
// ============================================================================

export type ValueFormat = 'number' | 'percent' | 'degrees' | 'kelvin' | 'feet' | 'inches' | 'times' | 'pixels';

interface ControlBase {
  key: RenderSettingKey;
  label: string;
  /** One line on what it does, shown as a tooltip. */
  hint?: string;
}
export interface RangeControl extends ControlBase {
  type: 'range';
  min: number;
  max: number;
  step: number;
  format?: ValueFormat;
}
export interface ToggleControl extends ControlBase {
  type: 'toggle';
}
export interface SelectControl extends ControlBase {
  type: 'select';
  options: { value: string | number; label: string }[];
}
export interface ColorControl extends ControlBase {
  type: 'color';
}
/**
 * A picture to draw a material with: one of a few built-in choices, or one the
 * user uploaded (textureLibrary.ts). The value is the built-in's `value` or the
 * upload's id.
 */
export interface TextureControl extends ControlBase {
  type: 'texture';
  builtins: { value: string; label: string }[];
}
export type ControlDef = RangeControl | ToggleControl | SelectControl | ColorControl | TextureControl;

/** A named bundle of values a group offers as a one-click starting point (a species of wood). */
export interface GroupPreset {
  label: string;
  values: Partial<RenderSettings>;
}

export interface ControlGroup {
  title: string;
  /** A boolean setting that switches the whole group on and off (shown in its heading). */
  toggle?: RenderSettingKey;
  /** One-click starting points, shown as buttons above the controls. */
  presets?: GroupPreset[];
  /** A line of explanation under the heading. */
  note?: string;
  controls: ControlDef[];
}

export type RenderWindowId = 'lighting' | 'shadows' | 'materials' | 'framing' | 'wood' | 'effects' | 'output';

export interface RenderWindowDef {
  id: RenderWindowId;
  title: string;
  groups: ControlGroup[];
}

const range = (key: RenderSettingKey, label: string, min: number, max: number, step: number, format?: ValueFormat, hint?: string): RangeControl =>
  ({ type: 'range', key, label, min, max, step, format, hint });
const toggle = (key: RenderSettingKey, label: string, hint?: string): ToggleControl => ({ type: 'toggle', key, label, hint });
const select = (key: RenderSettingKey, label: string, options: SelectControl['options'], hint?: string): SelectControl =>
  ({ type: 'select', key, label, options, hint });
const color = (key: RenderSettingKey, label: string, hint?: string): ColorControl => ({ type: 'color', key, label, hint });
const texture = (key: RenderSettingKey, label: string, builtins: TextureControl['builtins'], hint?: string): TextureControl =>
  ({ type: 'texture', key, label, builtins, hint });

const SIMULATED = [{ value: '', label: 'Simulated grain' }];
const UPLOAD_NOTE = 'Upload a photo of real wood to hold against the simulated grain: pick it under Texture, switch back to Simulated to compare. Uploads stay in this browser.';

const LUMBER_SPECIES: GroupPreset[] = [
  { label: 'SPF', values: { lumberColor: '#dcc096', lumberGrainLines: 8, lumberGrainContrast: 0.13, lumberLineWidth: 0.45, lumberWaviness: 0.16, lumberFibre: 0.05, lumberKnots: 4, lumberKnotDarkness: 0.22 } },
  { label: 'Douglas fir', values: { lumberColor: '#d8a26b', lumberGrainLines: 15, lumberGrainContrast: 0.2, lumberLineWidth: 0.35, lumberWaviness: 0.09, lumberFibre: 0.06, lumberKnots: 2, lumberKnotDarkness: 0.26 } },
  { label: 'Cedar', values: { lumberColor: '#c98d5e', lumberGrainLines: 11, lumberGrainContrast: 0.15, lumberLineWidth: 0.5, lumberWaviness: 0.2, lumberFibre: 0.08, lumberKnots: 5, lumberKnotDarkness: 0.3 } },
  { label: 'Treated', values: { lumberColor: '#b9b187', lumberGrainLines: 8, lumberGrainContrast: 0.12, lumberLineWidth: 0.5, lumberWaviness: 0.16, lumberFibre: 0.05, lumberKnots: 4, lumberKnotDarkness: 0.24 } },
];

const DOOR_SPECIES: GroupPreset[] = [
  { label: 'Walnut', values: { doorWoodColor: '#7a5540', doorGrainLines: 12, doorGrainContrast: 0.16, doorLineWidth: 0.45, doorWaviness: 0.22, doorFibre: 0.09 } },
  { label: 'Oak', values: { doorWoodColor: '#b98a57', doorGrainLines: 17, doorGrainContrast: 0.2, doorLineWidth: 0.3, doorWaviness: 0.12, doorFibre: 0.15 } },
  { label: 'Cherry', values: { doorWoodColor: '#9b5b3d', doorGrainLines: 9, doorGrainContrast: 0.1, doorLineWidth: 0.5, doorWaviness: 0.2, doorFibre: 0.06 } },
  { label: 'Maple', values: { doorWoodColor: '#d8bc93', doorGrainLines: 8, doorGrainContrast: 0.07, doorLineWidth: 0.5, doorWaviness: 0.18, doorFibre: 0.05 } },
  { label: 'Mahogany', values: { doorWoodColor: '#70392a', doorGrainLines: 10, doorGrainContrast: 0.12, doorLineWidth: 0.45, doorWaviness: 0.15, doorFibre: 0.1 } },
  { label: 'Espresso', values: { doorWoodColor: '#3b2a22', doorGrainLines: 14, doorGrainContrast: 0.1, doorLineWidth: 0.4, doorWaviness: 0.14, doorFibre: 0.08 } },
];

export const ENVIRONMENT_OPTIONS: { value: EnvironmentId; label: string }[] = [
  { value: 'studio', label: 'Studio (neutral)' },
  { value: 'garage1', label: 'Garage 1 (boards + doors)' },
  { value: 'garage2', label: 'Garage 2 (SlatWall workshop)' },
  { value: 'garage3', label: 'Garage 3 (open door, daylight)' },
  { value: 'none', label: 'None' },
];

export const RENDER_WINDOWS: RenderWindowDef[] = [
  {
    id: 'lighting',
    title: 'Lighting',
    groups: [
      {
        title: 'Room light',
        controls: [
          // The Simple / Advanced choice at the top of the window (drawn there, not as a row).
          toggle('lightingSimple', 'Simple panel', 'Lay the room\'s lighting out from a few choices instead of every setting.'),
          select('interiorLayout', 'Layout', [
            { value: 'array', label: 'Fill the room (array)' },
            { value: 'grid', label: 'Grid of 4' },
            { value: 'row', label: 'In a row' },
          ], 'Array: the room filled evenly, about one lamp every so many feet (spacing, below) — never more than 16. Grid: four lamps where the lines of a noughts-and-crosses grid over the room cross. Row: lamps down the room\'s longer side (how many, below).'),
          range('interiorSpacing', 'Spacing', 4, 20, 0.5, 'feet', 'Array layout: about one lamp every so many feet, each way. The count follows the room\'s proportions.'),
          select('interiorFixtureStyle', 'Fixture', [
            { value: 'dome', label: 'Dome light' },
            { value: 'pot', label: 'Pot light (recessed)' },
            { value: 'bar', label: 'Shop light (bar)' },
          ], 'What each lamp looks like. Domes and pot lights sit on the ceiling — flat on it, whichever way it slopes; bars hang at the height below.'),
          select('interiorLamps', 'Lamps in a row', [
            { value: 1, label: '1 — centre of the room' },
            { value: 2, label: '2 in a row' },
            { value: 3, label: '3 in a row' },
            { value: 4, label: '4 in a row' },
          ], 'Lamps hang in a row down the room\'s longer side. More lamps light the walls more evenly; each one casts its own shadows.'),
          range('interiorIntensity', 'Brightness', 0, 1.5, 0.01, 'number', 'All the lamps together. 1.0 would light the nearest wall to full white from one lamp alone.'),
          range('interiorWarmth', 'Warmth', 2700, 7500, 50, 'kelvin', 'Colour temperature. 6500 K is pure white and tints nothing; lower is warmer.'),
          range('interiorHeight', 'Height', 0.1, 1, 0.01, 'percent', 'How high the lamp hangs, as a share of the room height.'),
          range('interiorX', 'Position along length', 0.05, 0.95, 0.01, 'percent', 'Where the lamp (or the middle of the row) sits along the room\'s length.'),
          range('interiorZ', 'Position along width', 0.05, 0.95, 0.01, 'percent'),
          range('interiorFalloff', 'Falloff', 0, 2, 0.05, 'number', 'How quickly the light fades with distance. 2 is physical; lower is more even.'),
          range('interiorReach', 'Reach', 0, 3, 0.05, 'times', 'Where the light cuts off, in room lengths. 0 = no cut-off.'),
          toggle('interiorFixtures', 'Show the light fixtures', 'A glowing shop light at each lamp. They show in glass reflections, and flare with Effects → Bloom. (Not drawn over a wall you are editing.)'),
        ],
      },
      {
        title: 'Ambient',
        controls: [
          range('ambientIntensity', 'Brightness', 0, 1.5, 0.01, 'number', 'Even light from everywhere. Lifts shadows; too much flattens the room.'),
          color('ambientSky', 'From above'),
          color('ambientGround', 'From below', 'Bounce light off the floor. A tint here tints the ceiling.'),
        ],
      },
      {
        title: 'Key light',
        controls: [
          range('keyIntensity', 'Brightness', 0, 1, 0.01, 'number', 'A directional light that gives the boards relief.'),
          select('keyMode', 'Direction', [
            { value: 'camera', label: 'Follows the camera' },
            { value: 'fixed', label: 'Fixed in the room' },
          ], 'Following the camera keeps whichever wall you look at evenly lit.'),
          range('keyAzimuth', 'Angle (side)', -180, 180, 1, 'degrees', 'Following the camera: how far to the side of your view. Fixed: compass direction.'),
          range('keyElevation', 'Angle (height)', -10, 85, 1, 'degrees'),
          range('keyWarmth', 'Warmth', 2700, 7500, 50, 'kelvin'),
          range('fillIntensity', 'Fill from the other side', 0, 0.5, 0.01, 'number', 'A weaker light opposite the key, so the far side is not left dark.'),
        ],
      },
      {
        title: 'Environment',
        controls: [
          select('environment', 'Reflects', ENVIRONMENT_OPTIONS, 'What the boards reflect, and a share of the ambient light. Photos tint the boards; Studio does not.'),
          range('envIntensity', 'Strength', 0, 2, 0.01, 'number'),
          range('probeIntensity', 'Extra ambient (legacy probe)', 0, 1, 0.01, 'number', 'The old visualizer\'s ambient term. Only the Classic look uses it.'),
        ],
      },
    ],
  },
  {
    id: 'shadows',
    title: 'Shadows',
    groups: [
      {
        title: 'Cast shadows',
        toggle: 'shadows',
        controls: [
          select('shadowType', 'Type', [
            { value: 'vsm', label: 'Soft (VSM)' },
            { value: 'pcfsoft', label: 'Smooth (PCF soft)' },
            { value: 'pcf', label: 'Filtered (PCF)' },
            { value: 'basic', label: 'Hard' },
          ]),
          range('shadowSoftness', 'Softness', 0, 12, 0.1, 'number', 'Blur radius. Soft and Filtered types only.'),
          select('shadowResolution', 'Resolution', [
            { value: 512, label: '512 (fast)' },
            { value: 1024, label: '1024' },
            { value: 2048, label: '2048' },
            { value: 4096, label: '4096 (sharp, heavy)' },
          ]),
          range('shadowBias', 'Bias', -0.01, 0.01, 0.0001, 'number', 'Nudge this if shadows show stripes (acne) or float off their object.'),
          range('shadowNormalBias', 'Normal bias', 0, 0.3, 0.005, 'number'),
          toggle('interiorOtherShadows', 'Other rooms\' lamps cast shadows', 'Floor plans: the lamps in the rooms around the one shown cast shadows too (at a lower resolution), so walls between rooms stop their light. Off is lighter on a slow machine.'),
        ],
      },
      {
        title: 'Ambient occlusion',
        toggle: 'ao',
        controls: [
          range('aoIntensity', 'Strength', 0, 2, 0.01, 'number', 'Darkens corners and creases where light would not reach — the biggest single realism gain.'),
          range('aoRadius', 'Radius', 0.1, 3, 0.05, 'feet', 'How far from a corner the darkening spreads.'),
          range('aoFalloff', 'Falloff', 0.1, 2, 0.05, 'number'),
          range('aoThickness', 'Thickness', 0.1, 3, 0.05, 'number', 'How thick thin things (trim, studs) are assumed to be.'),
          select('aoSamples', 'Quality', [
            { value: 8, label: 'Low (8 samples)' },
            { value: 16, label: 'Medium (16)' },
            { value: 24, label: 'High (24)' },
            { value: 32, label: 'Ultra (32)' },
          ]),
          toggle('aoHalfRes', 'Half resolution', 'Much faster, slightly softer.'),
        ],
      },
      {
        title: 'Ground',
        controls: [
          range('groundShadow', 'Shadow under the room', 0, 1, 0.01, 'number', 'A soft shadow on the backdrop around the room\'s footprint.'),
        ],
      },
    ],
  },
  {
    id: 'materials',
    title: 'Materials',
    groups: [
      {
        title: 'Bare areas',
        note: 'What a wall or the ceiling shows where no product has been put on it.',
        controls: [
          select('bareWallFill', 'Walls', [
            { value: 'studs', label: 'Bare studs, with the sheet behind them' },
            { value: 'drywall', label: 'Unpainted drywall — mud and tape' },
            { value: 'grid', label: 'Grey grid' },
          ], 'Studs: the 2x4s show where the wall is bare, framing shown or not, with the paper back of a sheet behind them so you never see straight through. Drywall: taped and mudded, unpainted. Grid: a neutral stand-in.'),
          select('bareCeilingFill', 'Ceiling', [
            { value: 'studs', label: 'Bare joists, with the sheet above them' },
            { value: 'drywall', label: 'Unpainted drywall — mud and tape' },
            { value: 'grid', label: 'Grey grid' },
          ]),
        ],
      },
      {
        title: 'Boards and trim',
        controls: [
          range('boardRoughness', 'Roughness', 0, 1, 0.01, 'number', '0 is a mirror gloss, 1 is chalk-matte.'),
          range('boardMetalness', 'Metalness', 0, 1, 0.01, 'number'),
          range('boardGlow', 'Self-glow', 0, 0.6, 0.01, 'number', 'Light the boards give off by themselves. Keeps colour bright in shadow, but too much washes out all shading — the old look used 0.40.'),
          range('boardBump', 'Surface texture', 0, 2, 0.01, 'number', 'Depth of the fine striations on Wall&CeilingBoard.'),
        ],
      },
      {
        title: 'Seams',
        controls: [
          range('seamStrength', 'Strength', 0, 1, 0.01, 'number', 'How dark the joint between boards reads — the same in any light, however bright the wall. 0 hides the seams.'),
          range('seamWidth', 'Width', 0.03, 0.5, 0.005, 'inches', 'The real joint is about 1/8". Seams are drawn at their true width, so they thin out with distance like the real thing.'),
          color('seamColor', 'Colour'),
        ],
      },
      {
        title: 'Glass',
        note: 'Windows, and the glass in doors and garage doors.',
        controls: [
          color('glassTint', 'Tint'),
          range('glassOpacity', 'Tint strength', 0, 0.8, 0.01, 'percent', 'How much the glass itself shows. Low is clear: you see straight through to what is outside.'),
          range('glassReflection', 'Reflection', 0, 25, 0.1, 'number', 'How strongly the glass mirrors the room: the other walls, the floor, the light fixtures. 1 is real glass (faint head-on, strong at a glancing angle); higher exaggerates it. 0 turns the mirror off.'),
          range('glassGlare', 'Glare', 0, 8, 0.05, 'number', 'The hot spots of the room\'s lamps on the glass. For glare that flares, switch on Effects → Bloom.'),
          range('glassGlareSize', 'Glare size', 0.01, 0.5, 0.01, 'number', 'Small and sharp, or broad and soft.'),
        ],
      },
      {
        title: 'Blueprint ground',
        toggle: 'blueprint',
        note: 'The Floor Planner\'s sheet, under the rooms: its grid lines up with the planner\'s. The rooms\' shadows fall on it.',
        controls: [
          color('blueprintColor', 'Sheet colour'),
          range('blueprintOpacity', 'Sheet opacity', 0, 1, 0.01, 'percent', '0 leaves just the grid lines over the backdrop.'),
          color('blueprintLineColor', 'Line colour'),
          range('blueprintLineOpacity', 'Line opacity', 0, 1, 0.01, 'percent'),
          range('blueprintLineWidth', 'Line weight', 0.25, 4, 0.05, 'pixels', 'Lines are drawn a constant width on screen, like ink, however far away.'),
          range('blueprintMajorEvery', 'Bold line every', 0, 20, 1, 'feet', '0 = no bold lines.'),
          range('blueprintMajorWeight', 'Bold line weight', 1, 4, 0.05, 'times', 'How much heavier the bold lines are.'),
          range('blueprintReach', 'Reach', 2, 200, 1, 'feet', 'How far past the rooms the sheet runs before it fades away.'),
        ],
      },
      {
        title: 'Floor',
        controls: [
          texture('floorTexture', 'Texture', FLOOR_TEXTURES.map(({ value, label }) => ({ value, label })), 'One of the built-in floors, or a picture you upload.'),
          range('floorTextureSize', 'Width of one tile', 0.5, 30, 0.25, 'feet', 'How much floor the picture covers, across its width.'),
          toggle('floorTextureRotate', 'Turn a quarter', 'Run the picture the other way (boards across the room instead of along it).'),
          range('floorBrightness', 'Brightness', 0.3, 1.5, 0.01, 'number'),
          range('floorRoughness', 'Roughness', 0, 1, 0.01, 'number', 'Lower gives a sealed, polished-concrete sheen.'),
          range('floorReflection', 'Reflections', 0, 2, 0.01, 'number'),
        ],
      },
    ],
  },
  {
    id: 'framing',
    title: 'Framing',
    groups: [
      {
        title: 'Lumber',
        presets: LUMBER_SPECIES,
        controls: [
          color('lumberColor', 'Colour'),
          range('lumberRelief', 'Relief', 0, 1.5, 0.01, 'number', 'How much the grain catches the light.'),
        ],
      },
      {
        title: 'Grain',
        controls: [
          range('lumberGrainLines', 'Grain lines', 3, 30, 1, 'number', 'How many across a board\'s width. More = finer, closer grain.'),
          range('lumberLineWidth', 'Line thickness', 0, 1, 0.01, 'number', 'How much of each ring is dark line: hairlines at 0, broad bands at 1.'),
          range('lumberGrainContrast', 'Line darkness', 0, 0.4, 0.005, 'number'),
          range('lumberWaviness', 'Waviness', 0, 0.6, 0.01, 'number', 'How far the lines wander and how unevenly they are spaced. 0 is ruled straight.'),
          range('lumberFibre', 'Fibre', 0, 0.2, 0.005, 'number', 'Fine streaks along the board.'),
        ],
      },
      {
        title: 'Knots',
        controls: [
          range('lumberKnots', 'How many', 0, 10, 1, 'number', 'In about three feet of board.'),
          range('lumberKnotDarkness', 'Darkness', 0, 0.6, 0.01, 'number'),
        ],
      },
      {
        title: 'Real texture',
        note: UPLOAD_NOTE,
        controls: [
          texture('lumberTexture', 'Texture', SIMULATED),
          range('lumberTextureSize', 'Width of the picture', 0.1, 8, 0.05, 'feet', 'How much real wood the picture shows, across its width.'),
          toggle('lumberTextureRotate', 'Turn a quarter', 'For a picture whose grain runs side to side.'),
          range('lumberTextureTint', 'Tint with the colour', 0, 1, 0.01, 'percent', '0 shows the picture as it is; 1 multiplies it by the lumber colour.'),
        ],
      },
    ],
  },
  {
    id: 'wood',
    title: 'Wood doors',
    groups: [
      {
        title: 'Species',
        presets: DOOR_SPECIES,
        note: 'For the wooden styles: Shaker — walnut, and the Carriage garage door.',
        controls: [
          color('doorWoodColor', 'Colour'),
          range('doorRelief', 'Relief', 0, 1.5, 0.01, 'number', 'How much the grain catches the light.'),
        ],
      },
      {
        title: 'Grain',
        controls: [
          range('doorGrainLines', 'Grain lines', 3, 30, 1, 'number', 'How many across nine inches of board. More = finer, closer grain.'),
          range('doorLineWidth', 'Line thickness', 0, 1, 0.01, 'number', 'How much of each ring is dark line: hairlines at 0, broad bands at 1.'),
          range('doorGrainContrast', 'Line darkness', 0, 0.4, 0.005, 'number'),
          range('doorWaviness', 'Waviness', 0, 0.6, 0.01, 'number', 'How far the lines wander and how unevenly they are spaced. 0 is ruled straight.'),
          range('doorFibre', 'Fibre', 0, 0.2, 0.005, 'number', 'Fine streaks along the board.'),
        ],
      },
      {
        title: 'Real texture',
        note: UPLOAD_NOTE,
        controls: [
          texture('doorTexture', 'Texture', SIMULATED),
          range('doorTextureSize', 'Width of the picture', 0.1, 8, 0.05, 'feet', 'How much real wood the picture shows, across its width.'),
          toggle('doorTextureRotate', 'Turn a quarter', 'For a picture whose grain runs side to side.'),
          range('doorTextureTint', 'Tint with the colour', 0, 1, 0.01, 'percent', '0 shows the picture as it is; 1 multiplies it by the wood colour.'),
        ],
      },
    ],
  },
  {
    id: 'effects',
    title: 'Effects',
    groups: [
      {
        title: 'Colour',
        controls: [
          range('brightness', 'Brightness', -0.2, 0.2, 0.005, 'number'),
          range('contrast', 'Contrast', 0.7, 1.5, 0.01, 'number'),
          range('saturation', 'Saturation', 0, 2, 0.01, 'number'),
          range('temperature', 'Temperature', -1, 1, 0.01, 'number', 'Cooler (−) or warmer (+) over the whole picture.'),
        ],
      },
      {
        title: 'Bloom',
        toggle: 'bloom',
        controls: [
          range('bloomStrength', 'Strength', 0, 2, 0.01, 'number', 'A glow around the brightest areas. 1.0 adds a glow as bright as the area casting it.'),
          range('bloomRadius', 'Radius', 0, 1, 0.01, 'number'),
          range('bloomThreshold', 'Threshold', 0, 2, 0.01, 'number', 'Only areas brighter than this glow. A fully lit white board is about 1.0.'),
          range('bloomKnee', 'Ease-in', 0.01, 2, 0.01, 'number', 'How far above the threshold an area must be to glow in full. Low is a cliff: a wall barely over the threshold glows like a lamp and fogs whatever is next to it. Keep it high for white walls.'),
        ],
      },
      {
        title: 'Lens',
        controls: [
          range('vignette', 'Vignette', 0, 1, 0.01, 'number', 'Darkens the corners of the picture.'),
          range('vignetteSoftness', 'Vignette softness', 0.1, 1, 0.01, 'number'),
          range('grain', 'Film grain', 0, 0.15, 0.001, 'number', 'Fine noise. A little hides banding in smooth white gradients.'),
          range('softness', 'Softness', 0, 1, 0.01, 'number', 'A lens\'s softness: the picture blurred a touch, before sharpening. A little reads as a photograph.'),
          range('sharpen', 'Sharpen', 0, 1, 0.01, 'number'),
        ],
      },
      {
        title: 'Backdrop',
        controls: [
          color('backdropTop', 'Top'),
          color('backdropBottom', 'Bottom'),
        ],
      },
      {
        title: 'Logo',
        toggle: 'logo',
        controls: [
          select('logoVersion', 'Version', [
            { value: 'icon', label: 'Square icon' },
            { value: 'light', label: 'Full logo — light (white lettering)' },
            { value: 'dark', label: 'Full logo — dark (navy lettering)' },
          ], 'Light is for a dark backdrop, dark for a light one. The square icon works on either.'),
          select('logoPosition', 'Position', [
            { value: 'bottom-left', label: 'Bottom left' },
            { value: 'bottom-right', label: 'Bottom right' },
            { value: 'top-left', label: 'Top left' },
            { value: 'top-right', label: 'Top right' },
          ]),
          range('logoMargin', 'Distance from the corner', 0, 120, 1, 'pixels'),
          range('logoScale', 'Scale', 0.25, 4, 0.05, 'times'),
          range('logoOpacity', 'Opacity', 0, 1, 0.01, 'percent'),
        ],
      },
    ],
  },
  {
    id: 'output',
    title: 'Camera',
    groups: [
      {
        title: 'Exposure',
        controls: [
          select('toneMapping', 'Tone curve', [
            { value: 'accurate', label: 'Accurate (soft highlights)' },
            { value: 'linear', label: 'Linear (hard clip)' },
            { value: 'neutral', label: 'Neutral (Khronos)' },
            { value: 'aces', label: 'ACES filmic' },
            { value: 'agx', label: 'AgX' },
            { value: 'reinhard', label: 'Reinhard' },
            { value: 'cineon', label: 'Cineon' },
          ], 'How light becomes screen brightness. Accurate keeps product colours exact and only eases the brightest highlights; the filmic curves trade accuracy for drama.'),
          toggle('autoExposure', 'Auto exposure', 'Metered from the picture, as the render is: the lit boards brought to one level whatever the lights. Exposure below is then an offset from it.'),
          range('exposure', 'Exposure', 0.3, 2.5, 0.01, 'times'),
          range('highlightRolloff', 'Highlight roll-off', 0, 0.4, 0.01, 'number', 'Accurate curve only: how gently the brightest whites ease toward pure white instead of clipping. 0 = hard clip.'),
        ],
      },
      {
        title: 'Quality',
        controls: [
          select('renderScale', 'Render scale', [
            { value: 0, label: 'Auto (2× or better)' },
            { value: 1, label: '1× (fastest)' },
            { value: 1.5, label: '1.5×' },
            { value: 2, label: '2×' },
            { value: 3, label: '3× (heavy)' },
          ], 'Pixels rendered per screen pixel. Higher is crisper and slower.'),
          select('antialias', 'Anti-aliasing', [
            { value: 0, label: 'Off' },
            { value: 2, label: '2×' },
            { value: 4, label: '4×' },
            { value: 8, label: '8× (heavy)' },
          ]),
        ],
      },
    ],
  },
];

const ALL_CONTROLS: ControlDef[] = RENDER_WINDOWS.flatMap((w) => w.groups.flatMap((g) => g.controls));
const CONTROL_BY_KEY = new Map<RenderSettingKey, ControlDef>(ALL_CONTROLS.map((c) => [c.key, c]));

export function getControl(key: RenderSettingKey): ControlDef | undefined {
  return CONTROL_BY_KEY.get(key);
}

// ============================================================================
// SANITISING — nothing out of range ever reaches the renderer
// ============================================================================

const HEX = /^#[0-9a-f]{6}$/i;

function sanitiseValue(key: RenderSettingKey, value: unknown): RenderSettings[RenderSettingKey] | undefined {
  const fallback = DEFAULT_RENDER_SETTINGS[key];
  const control = CONTROL_BY_KEY.get(key);
  if (typeof fallback === 'boolean') return typeof value === 'boolean' ? value : undefined;
  if (control?.type === 'select') {
    const match = control.options.find((o) => o.value === value || String(o.value) === String(value));
    return match ? (match.value as RenderSettings[RenderSettingKey]) : undefined;
  }
  if (control?.type === 'color') {
    return typeof value === 'string' && HEX.test(value) ? value.toLowerCase() : undefined;
  }
  if (control?.type === 'texture') {
    // A built-in, or an upload's id (letters, digits, dashes). Whether that upload
    // still exists is the pipeline's to find out; it falls back if not.
    return typeof value === 'string' && /^[a-z0-9-]{0,80}$/i.test(value) ? value : undefined;
  }
  if (control?.type === 'range') {
    const n = typeof value === 'string' ? Number(value) : value;
    if (typeof n !== 'number' || !Number.isFinite(n)) return undefined;
    return Math.min(control.max, Math.max(control.min, n));
  }
  return undefined;
}

/** A full, in-range settings object from anything: unknown keys dropped, bad or missing values defaulted. */
export function sanitiseRenderSettings(input: unknown, base: RenderSettings = DEFAULT_RENDER_SETTINGS): RenderSettings {
  const out: RenderSettings = { ...base };
  if (!input || typeof input !== 'object') return out;
  const source = input as Record<string, unknown>;
  for (const key of Object.keys(DEFAULT_RENDER_SETTINGS) as RenderSettingKey[]) {
    if (!(key in source)) continue;
    const clean = sanitiseValue(key, source[key]);
    if (clean !== undefined) (out as unknown as Record<string, unknown>)[key] = clean;
  }
  return out;
}

// ============================================================================
// LOOKS — named starting points
// ============================================================================

export interface RenderPreset {
  id: string;
  label: string;
  description: string;
  settings: RenderSettings;
}

// The looks are changes from the FACTORY look, so each is exactly what it
// says (the colour check really shades nothing) whatever the shipped default
// has been tuned to. Studio IS the shipped default.
const look = (id: string, label: string, description: string, overrides: Partial<RenderSettings>): RenderPreset =>
  ({ id, label, description, settings: { ...FACTORY_RENDER_SETTINGS, ...overrides } });

export const RENDER_PRESETS: RenderPreset[] = [
  { id: 'studio', label: 'Studio', description: 'The default. Even, neutral light; soft contact shadows; seams just visible.', settings: { ...DEFAULT_RENDER_SETTINGS } },
  look('daylight', 'Daylight', 'Cool light from one side, as if through an open garage door. The wall it falls on is bright; the one behind it is in shade.', {
    interiorIntensity: 0.12,
    ambientIntensity: 0.58,
    ambientSky: '#f4f8ff',
    ambientGround: '#ece8e0',
    keyIntensity: 0.32,
    keyMode: 'fixed',
    keyAzimuth: 50,
    keyElevation: 38,
    keyWarmth: 7000,
    fillIntensity: 0.14,
    environment: 'garage3',
    envIntensity: 0.2,
    backdropTop: '#f3f7fb',
    backdropBottom: '#dfe6ee',
  }),
  look('evening', 'Warm evening', 'Warm lamps, deeper shadows, a little glow.', {
    interiorIntensity: 0.9,
    interiorWarmth: 3600,
    interiorFalloff: 1,
    ambientIntensity: 0.26,
    ambientGround: '#e2d6c6',
    keyIntensity: 0.06,
    fillIntensity: 0.02,
    envIntensity: 0.15,
    aoIntensity: 1.25,
    bloom: true,
    bloomStrength: 0.6,
    bloomThreshold: 0.6,
    vignette: 0.3,
    backdropTop: '#efe9e1',
    backdropBottom: '#d8cfc3',
  }),
  look('showroom', 'Showroom', 'High contrast on a dark backdrop, for screenshots.', {
    interiorIntensity: 0.4,
    interiorFalloff: 1.1,
    ambientIntensity: 0.3,
    keyIntensity: 0.2,
    envIntensity: 0.35,
    boardRoughness: 0.3,
    aoIntensity: 1.3,
    aoRadius: 2,
    contrast: 1.06,
    bloom: true,
    bloomStrength: 0.5,
    vignette: 0.3,
    vignetteSoftness: 0.6,
    grain: 0.018,
    groundShadow: 0.6,
    backdropTop: '#3a3d44',
    backdropBottom: '#17181b',
  }),
  look('flat', 'Flat (colour check)', 'No shading at all: every board is exactly its product colour. For checking colours, not for looking at.', {
    interiorIntensity: 0,
    interiorFixtures: false,
    ambientIntensity: 1,
    ambientGround: '#ffffff',
    keyIntensity: 0,
    fillIntensity: 0,
    environment: 'none',
    envIntensity: 0,
    shadows: false,
    ao: false,
    boardGlow: 0,
    boardBump: 0,
    boardRoughness: 1,
    vignette: 0,
    grain: 0,
    sharpen: 0,
    groundShadow: 0,
    toneMapping: 'linear',
    backdropBottom: '#f9fafb',
  }),
  look('classic', 'Classic', 'How the visualizer looked before these controls: bright self-lit boards, no occlusion, hard clip.', {
    interiorLamps: 1,
    interiorFixtures: false,
    interiorIntensity: 0.99,
    interiorWarmth: 5600,
    interiorHeight: 0.75,
    interiorFalloff: 0.25,
    interiorReach: 0.65,
    ambientIntensity: 0.016,
    ambientSky: '#ffffff',
    ambientGround: '#f0f0f0',
    keyIntensity: 0.095,
    keyMode: 'fixed',
    keyAzimuth: 36,
    keyElevation: 49,
    keyWarmth: 6500,
    fillIntensity: 0.048,
    environment: 'garage3',
    envIntensity: 0.5,
    probeIntensity: 0.4,
    shadowSoftness: 2.5,
    ao: false,
    groundShadow: 0,
    boardRoughness: 0.3,
    boardMetalness: 0.1,
    boardGlow: 0.4,
    seamStrength: 0,
    floorRoughness: 1,
    floorReflection: 2,
    vignette: 0,
    grain: 0,
    sharpen: 0,
    backdropBottom: '#f9fafb',
    toneMapping: 'linear',
  }),
];

export const DEFAULT_PRESET_ID = 'studio';
/** The look "Compare" flips to, to show the view as it was before. */
export const COMPARE_PRESET_ID = 'classic';

export function getRenderPreset(id: string): RenderPreset | undefined {
  return RENDER_PRESETS.find((p) => p.id === id);
}

function sameSettings(a: RenderSettings, b: RenderSettings): boolean {
  return (Object.keys(DEFAULT_RENDER_SETTINGS) as RenderSettingKey[]).every((k) => a[k] === b[k]);
}

/** The same look? (The logo and the quality settings are not part of a look.) */
function sameLook(a: RenderSettings, b: RenderSettings): boolean {
  return (Object.keys(DEFAULT_RENDER_SETTINGS) as RenderSettingKey[])
    .every((k) => LOOK_INDEPENDENT_KEYS.includes(k) || a[k] === b[k]);
}

/** The look these settings are, exactly — or null once anything of the look has been changed by hand. */
export function matchingPresetId(settings: RenderSettings): string | null {
  return RENDER_PRESETS.find((p) => sameLook(p.settings, settings))?.id ?? null;
}

/** `look` with the logo and quality settings of `current` — a look applied without disturbing those. */
export function withLook(current: RenderSettings, look: RenderSettings): RenderSettings {
  const next: RenderSettings = { ...look };
  for (const key of LOOK_INDEPENDENT_KEYS) (next as unknown as Record<string, unknown>)[key] = current[key];
  return next;
}

// ============================================================================
// STORE
// ============================================================================

export const RENDER_SETTINGS_STORAGE_KEY = 'designer.render.v1';
const STORAGE_VERSION = 1;

function readStored(): RenderSettings {
  try {
    if (typeof localStorage === 'undefined') return DEFAULT_RENDER_SETTINGS;
    const raw = localStorage.getItem(RENDER_SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_RENDER_SETTINGS;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== STORAGE_VERSION) return DEFAULT_RENDER_SETTINGS;
    return sanitiseRenderSettings(parsed.settings);
  } catch {
    return DEFAULT_RENDER_SETTINGS;
  }
}

function writeStored(settings: RenderSettings): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(RENDER_SETTINGS_STORAGE_KEY, JSON.stringify({ v: STORAGE_VERSION, settings }));
  } catch {
    /* storage full or blocked — the look just won't survive a reload */
  }
}

let saved: RenderSettings = readStored();
/** A look shown temporarily (Compare) without replacing what is saved. */
let preview: RenderSettings | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

/** What the renderer should draw with right now (the preview, while one is up). */
export function getRenderSettings(): RenderSettings {
  return preview ?? saved;
}

/** What is saved — what the controls show and edit — regardless of any preview. */
export function getSavedRenderSettings(): RenderSettings {
  return saved;
}

export function onRenderSettingsChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function commit(next: RenderSettings): void {
  if (sameSettings(next, saved)) return;
  saved = next;
  writeStored(saved);
  notify();
}

export function updateRenderSettings(patch: Partial<RenderSettings>): void {
  commit(sanitiseRenderSettings(patch, saved));
}

export function setRenderSetting<K extends RenderSettingKey>(key: K, value: RenderSettings[K]): void {
  updateRenderSettings({ [key]: value } as Partial<RenderSettings>);
}

export function applyRenderPreset(id: string): boolean {
  const preset = getRenderPreset(id);
  if (!preset) return false;
  commit(withLook(saved, preset.settings));
  return true;
}

export function resetRenderSettings(): void {
  commit({ ...DEFAULT_RENDER_SETTINGS });
}

/** Show a look without saving it (null puts the saved one back). */
export function previewRenderSettings(settings: RenderSettings | null): void {
  if (preview === settings) return;
  preview = settings;
  notify();
}

export function isPreviewingRenderSettings(): boolean {
  return preview !== null;
}

/**
 * The saved settings as JSON — what differs from the FACTORY look, so it reads
 * as a recipe, and a whole one: pasted over looks/default.json it is the new default.
 */
export function exportRenderSettings(): string {
  const diff: Partial<Record<RenderSettingKey, unknown>> = {};
  for (const key of Object.keys(FACTORY_RENDER_SETTINGS) as RenderSettingKey[]) {
    if (saved[key] !== FACTORY_RENDER_SETTINGS[key]) diff[key] = saved[key];
  }
  return JSON.stringify({ trusscoreRenderSettings: STORAGE_VERSION, basedOn: 'factory', changes: diff }, null, 2);
}

/**
 * Load settings from JSON made by exportRenderSettings (a recipe: changes from
 * the factory look) or a plain object of settings. Returns false, changing
 * nothing, if the text is not usable.
 */
export function importRenderSettings(text: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return false;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
  const obj = parsed as Record<string, unknown>;
  const changes = obj.changes && typeof obj.changes === 'object' ? obj.changes : obj;
  const known = Object.keys(changes as object).some((k) => k in DEFAULT_RENDER_SETTINGS);
  if (!known && Object.keys(changes as object).length > 0) return false;
  commit(sanitiseRenderSettings(changes, FACTORY_RENDER_SETTINGS));
  return true;
}

/** Tests only: forget the saved look and any preview, and re-read storage. */
export function reloadRenderSettingsForTest(): void {
  preview = null;
  saved = readStored();
}

// Dev builds: reachable from the console and from test tooling, like
// window.__VISUALIZER_DEBUG__ (visualizerDebug.ts).
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  (window as unknown as { __RENDER_SETTINGS__: unknown }).__RENDER_SETTINGS__ = {
    get: getRenderSettings,
    update: updateRenderSettings,
    preset: applyRenderPreset,
    reset: resetRenderSettings,
    export: exportRenderSettings,
  };
}

// ============================================================================
// HELPERS shared by the pipeline and the controls
// ============================================================================

/** Tanner Helland's fit of black-body colour to the CIE data, 0..1 per channel. */
function blackBody(kelvin: number): [number, number, number] {
  const t = Math.min(40000, Math.max(1000, kelvin)) / 100;
  let r: number;
  let g: number;
  let b: number;
  if (t <= 66) {
    r = 255;
    g = 99.4708025861 * Math.log(t) - 161.1195681661;
    b = t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  } else {
    r = 329.698727446 * Math.pow(t - 60, -0.1332047592);
    g = 288.1221695283 * Math.pow(t - 60, -0.0755148492);
    b = 255;
  }
  const clamp = (v: number) => Math.min(255, Math.max(0, v)) / 255;
  return [clamp(r), clamp(g), clamp(b)];
}

/** The white point: a light at this temperature is exactly white, so it tints nothing. */
export const NEUTRAL_KELVIN = 6500;

/**
 * Colour of a light at `kelvin`, as display RGB 0..1 with the brightest channel
 * at 1. Relative to NEUTRAL_KELVIN, which comes out as pure white: lower is
 * warmer (amber), higher cooler (blue).
 */
export function kelvinToRgb(kelvin: number): [number, number, number] {
  const raw = blackBody(kelvin);
  const white = blackBody(NEUTRAL_KELVIN);
  const rgb = raw.map((v, i) => v / white[i]);
  const peak = Math.max(...rgb) || 1;
  return [rgb[0] / peak, rgb[1] / peak, rgb[2] / peak];
}

/** '#rrggbb' → display RGB 0..1. */
export function hexToRgb(hex: string): [number, number, number] {
  const m = HEX.test(hex) ? hex.slice(1) : 'ffffff';
  return [0, 2, 4].map((i) => parseInt(m.slice(i, i + 2), 16) / 255) as [number, number, number];
}

/** A value as the controls show it. */
export function formatSettingValue(value: number, control: RangeControl): string {
  const decimals = Math.max(0, Math.min(4, Math.ceil(-Math.log10(control.step))));
  switch (control.format) {
    case 'percent': return `${Math.round(value * 100)}%`;
    case 'degrees': return `${Math.round(value)}°`;
    case 'kelvin': return `${Math.round(value)} K`;
    case 'feet': return `${value.toFixed(2)} ft`;
    case 'inches': return `${value.toFixed(3)}"`;
    case 'times': return `${value.toFixed(2)}×`;
    case 'pixels': return `${Math.round(value)} px`;
    default: return value.toFixed(decimals);
  }
}
